import { Table, Button, Modal, Typography, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Eye, Download, Smartphone } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useQuery } from 'react-query'

import { mobileClientLogsApi, type MobileClientLogFileSummary } from '../services/api'

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function extractErrorEntries(data: unknown): unknown[] {
  if (!data || typeof data !== 'object') return []
  const entries = (data as { entries?: unknown }).entries
  if (!Array.isArray(entries)) return []
  return entries.filter((e) => {
    if (!e || typeof e !== 'object') return false
    const level = (e as { level?: string }).level
    return level === 'error'
  })
}

const MobileClientLogsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [viewFilename, setViewFilename] = useState<string | null>(null)

  const { data: listData, isLoading } = useQuery(
    ['mobile-client-logs', page, limit],
    () => mobileClientLogsApi.listFiles({ page, limit }),
    { keepPreviousData: true }
  )

  const { data: detailData, isFetching: detailLoading } = useQuery(
    ['mobile-client-log-file', viewFilename ?? ''],
    async () => {
      const fn = viewFilename
      if (!fn) return { success: true as const, data: {} as unknown }
      return mobileClientLogsApi.getFile(fn)
    },
    { enabled: !!viewFilename }
  )

  const errorSubset = useMemo(
    () => (detailData?.data ? extractErrorEntries(detailData.data) : []),
    [detailData]
  )

  const columns: ColumnsType<MobileClientLogFileSummary> = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true
    },
    {
      title: 'Modified',
      dataIndex: 'modified',
      key: 'modified',
      width: 220,
      render: (v: string) => new Date(v).toLocaleString()
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (n: number) => formatBytes(n)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <Space>
          <Button type="link" size="small" icon={<Eye className="h-4 w-4" />} onClick={() => setViewFilename(row.filename)}>
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<Download className="h-4 w-4" />}
            onClick={async () => {
              const res = await mobileClientLogsApi.getFile(row.filename)
              const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = row.filename
              a.click()
              URL.revokeObjectURL(url)
            }}
          >
            Download
          </Button>
        </Space>
      )
    }
  ]

  const files = listData?.files ?? []
  const total = listData?.total ?? 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Smartphone className="h-8 w-8 text-primary-600" />
          Mobile client logs
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          JSON batches written when the API falls back to disk (e.g. dev or when Google Cloud Logging fails). In
          production, most mobile batches go to{' '}
          <Typography.Link href="https://console.cloud.google.com/logs/query" target="_blank" rel="noreferrer">
            Google Cloud Logging
          </Typography.Link>{' '}
          instead and may not appear here.
        </p>
      </div>

      <div className="rounded-lg bg-white shadow">
        <Table<MobileClientLogFileSummary>
          rowKey="filename"
          loading={isLoading}
          columns={columns}
          dataSource={files}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p)
          }}
          locale={{
            emptyText: total === 0 && !isLoading ? 'No log files on this server yet.' : undefined
          }}
        />
      </div>

      <Modal
        title={viewFilename ? `Log file: ${viewFilename}` : 'Log file'}
        open={!!viewFilename}
        onCancel={() => setViewFilename(null)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setViewFilename(null)}>
            Close
          </Button>
        ]}
      >
        {detailLoading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Typography.Text strong>Error-level entries</Typography.Text>{' '}
              <Tag color={errorSubset.length ? 'red' : 'default'}>{errorSubset.length}</Tag>
              <pre className="mt-2 max-h-48 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
                {errorSubset.length ? JSON.stringify(errorSubset, null, 2) : 'None'}
              </pre>
            </div>
            <div>
              <Typography.Text strong>Full batch JSON</Typography.Text>
              <pre className="mt-2 max-h-[420px] overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-800">
                {JSON.stringify(detailData?.data ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default MobileClientLogsPage
